import { useRef, useState, type CSSProperties } from 'react';
import { Icon, Text, icon } from '@nula/core-ui';
import styles from './hold-to-unlock-button.module.css';

const HOLD_DURATION = 800;

export interface HoldToUnlockButtonProps {
  /** Called after the user holds the button for HOLD_DURATION ms. */
  onUnlock: () => void;
}

/**
 * Locked-state alternative to the action cluster: a single full-width
 * pill the user must hold for HOLD_DURATION ms to unlock the target.
 *
 * Used by NodeFloatingToolbar / FrameFloatingToolbar / EdgeFloatingToolbar
 * when the underlying node/edge is `locked: true`. The toolbar swaps
 * this in instead of the regular Duplicate / Lock / Delete cluster so
 * the locked node has exactly one affordance: unlock it.
 */
export function HoldToUnlockButton({ onUnlock }: HoldToUnlockButtonProps) {
  const [holding, setHolding] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleHoldStart = (e: React.MouseEvent | React.TouchEvent) => {
    e.stopPropagation();
    setHolding(true);
    timerRef.current = setTimeout(() => {
      setHolding(false);
      onUnlock();
    }, HOLD_DURATION);
  };

  const handleHoldEnd = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setHolding(false);
  };

  return (
    <button
      type="button"
      className={`${styles.holdBtn} nopan nodrag`}
      style={{ '--hold-duration': `${HOLD_DURATION}ms` } as CSSProperties}
      aria-label="Hold to unlock"
      onMouseDown={handleHoldStart}
      onMouseUp={handleHoldEnd}
      onMouseLeave={handleHoldEnd}
      onTouchStart={handleHoldStart}
      onTouchEnd={handleHoldEnd}
    >
      <Icon name="lock" size={icon.md} fill />
      <Text size="sm" className={styles.holdLabel}>Hold to unlock</Text>
      {holding && <span className={styles.holdProgress} />}
    </button>
  );
}
